/**
 * The Quality, Readiness and Delivery sections of a project's Analytics
 * page. Each opens with its figures in a summary band, then cards in
 * the shared analytics grid; ``extra`` is rendered inside that grid so
 * an extension's panels share the columns.
 */
import { Link } from "react-router-dom";
import { cn } from "../../utils/bem";
import { SummaryStrip } from "../../components/SummaryStrip/SummaryStrip";
import { BarChart, DIVERGING, fmtInt, fmtPct } from "../../components/charts";
import { useMetrics } from "../../hooks/useMetrics";
import { AnalyticsGrid, AnalyticsPanel } from "../ProjectDashboard/AnalyticsPanel";
import "./ProjectInsights.prefix.css";

const block = cn("project-insights");

// Low agreement = red, mid = amber, high = green. Used for label bars + chips.
const agreementColor = (v) => {
  if (v == null) return "var(--color-neutral-emphasis)";
  if (v >= 0.8) return "var(--color-positive-surface, #2a9d8f)";
  if (v >= 0.6) return "var(--color-warning-surface, #e9c46a)";
  return "var(--color-negative-surface, #e76f51)";
};

const shortName = (n) => ((n || "").length > 12 ? `${(n || "").slice(0, 11)}…` : n);

const ConfusionHeatmap = ({ categories, matrix }) => {
  const max = Math.max(1, ...matrix.flat());
  return (
    <div className={block.elem("matrix-wrap").toClassName()}>
      <table className={block.elem("matrix").toClassName()}>
        <thead>
          <tr>
            <th />
            {categories.map((c) => (
              <th key={c} title={c}>
                {shortName(c)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.map((row, i) => (
            <tr key={i}>
              <th title={categories[i]}>{shortName(categories[i])}</th>
              {row.map((v, j) => {
                const t = v / max;
                const diag = i === j;
                const bg = diag
                  ? `rgba(42, 157, 143, ${0.15 + 0.6 * t})`
                  : v > 0
                    ? `rgba(231, 111, 81, ${0.1 + 0.55 * t})`
                    : "transparent";
                return (
                  <td key={j} className={block.elem("cell").mod({ diag }).toClassName()} style={{ background: bg }}>
                    {v || ""}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AnnotatorHeatmap = ({ am }) => {
  const names = am.names?.length ? am.names : am.raters;
  return (
    <div className={block.elem("matrix-wrap").toClassName()}>
      <table className={block.elem("matrix").toClassName()}>
        <thead>
          <tr>
            <th />
            {names.map((n, i) => (
              <th key={i} title={n}>
                {shortName(n)}
              </th>
            ))}
            <th>Peer avg</th>
          </tr>
        </thead>
        <tbody>
          {am.matrix.map((row, i) => (
            <tr key={i}>
              <th title={names[i]}>{shortName(names[i])}</th>
              {row.map((v, j) => (
                <td
                  key={j}
                  className={block
                    .elem("cell")
                    .mod({ diag: i === j })
                    .toClassName()}
                  style={{ background: v == null ? "transparent" : `rgba(42, 157, 143, ${0.1 + 0.7 * v})` }}
                >
                  {v == null ? "—" : Math.round(v * 100)}
                </td>
              ))}
              <td className={block.elem("peer").toClassName()}>
                {am.peer_mean?.[am.raters[i]] == null ? "—" : `${Math.round(am.peer_mean[am.raters[i]] * 100)}%`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/** A labelled bar: name (with a quiet qualifier), fill, value, support. */
const LabelRow = ({ name, qualifier, qualifierFirst = false, value, support, color }) => (
  <div className={block.elem("label-row").toClassName()}>
    <div className={block.elem("label-name").toClassName()}>
      {qualifierFirst && <span className={block.elem("label-field").toClassName()}>{qualifier}</span>}
      <span>{name}</span>
      {!qualifierFirst && qualifier && <span className={block.elem("label-field").toClassName()}>{qualifier}</span>}
    </div>
    <div className={block.elem("label-bar").toClassName()}>
      <div
        className={block.elem("label-fill").toClassName()}
        style={{ width: `${Math.round((value ?? 0) * 100)}%`, background: color ?? agreementColor(value) }}
      />
    </div>
    <div className={block.elem("label-val").toClassName()}>{value == null ? "—" : fmtPct(value)}</div>
    <div className={block.elem("label-support").toClassName()}>{support}</div>
  </div>
);

const Stat = ({ label, value, sub }) => (
  <div className={block.elem("stat").toClassName()}>
    <div className={block.elem("stat-val").toClassName()}>{value}</div>
    <div className={block.elem("stat-label").toClassName()}>{label}</div>
    {sub && <div className={block.elem("stat-sub").toClassName()}>{sub}</div>}
  </div>
);

const valid = (res, key) => (res.data && typeof res.data === "object" && key in res.data ? res.data : null);

// ── Quality ───────────────────────────────────────────────────────────────────

export const QualitySection = ({ project, extra = null }) => {
  const insights = useMetrics(
    project?.id ? "projectMetricsInsights" : null,
    project?.id ? { params: { pk: project.id } } : null,
    [project?.id],
  );
  const agreement = useMetrics(
    project?.id ? "projectMetricsAgreement" : null,
    project?.id ? { params: { pk: project.id } } : null,
    [project?.id],
  );
  if (!project?.id) return null;

  const d = valid(insights, "per_label");
  const ag = valid(agreement, "histogram");
  const scored = d?.scored_tasks ?? ag?.count ?? 0;
  const am = d?.annotator_matrix ?? { raters: [], matrix: [], names: [], peer_mean: {} };
  const loading = insights.loading || agreement.loading;

  const needsOverlap = (
    <>
      Needs 2 or more annotations on overlapping tasks.{" "}
      <Link to={`/projects/${project.id}/settings/quality`}>Raise the overlap in Quality settings</Link>, let annotators
      submit, then recompute.
    </>
  );

  return (
    <>
      <SummaryStrip
        items={[
          {
            label: "Agreement",
            value: ag?.coefficient != null ? fmtPct(ag.coefficient) : ag?.mean != null ? fmtPct(ag.mean) : "—",
            sub: ag?.count
              ? ag.coefficient != null
                ? `${ag.method}, chance-corrected`
                : ag.method
              : "not enough overlap yet",
          },
          {
            label: "Observed mean",
            value: ag?.mean == null ? "—" : fmtPct(ag.mean),
            sub: ag?.median == null ? undefined : `median ${fmtPct(ag.median)}`,
          },
          { label: "Scored tasks", value: fmtInt(scored), sub: "with 2 or more annotations" },
          { label: "Raters compared", value: fmtInt(am.raters?.length ?? 0), sub: "on shared tasks" },
          {
            label: "Hardest task",
            value: d?.hardest_tasks?.[0] ? fmtPct(d.hardest_tasks[0].agreement) : "—",
            sub: d?.hardest_tasks?.[0] ? `task ${d.hardest_tasks[0].task_id}` : undefined,
          },
        ]}
      />

      <AnalyticsGrid>
        {!ag || ag.count === 0 ? (
          <AnalyticsPanel
            title="Agreement"
            subtitle="How often annotators gave the same answer on the same task, by the method chosen in Quality settings."
            empty={loading ? "Loading…" : needsOverlap}
            span={2}
          />
        ) : (
          <AnalyticsPanel
            title="Agreement distribution"
            subtitle={
              ag.method === "consensus"
                ? "Consensus: how many annotators matched the plurality answer, per task."
                : ag.coefficient != null
                  ? `Per-task observed agreement. The ${ag.method} figure above is chance-corrected for the whole project.`
                  : "Pairwise: the average match between pairs of annotators, per task."
            }
          >
            <BarChart
              data={ag.histogram.map((r, i) => ({
                label: r.label,
                value: r.value,
                color:
                  DIVERGING[Math.min(DIVERGING.length - 1, Math.floor((i * DIVERGING.length) / ag.histogram.length))],
              }))}
              orientation="vertical"
              height={200}
              valueFormatter={fmtInt}
            />
          </AnalyticsPanel>
        )}

        {d && (d.field_agreement ?? []).length > 0 && (
          <AnalyticsPanel
            title="Field agreement"
            subtitle="Mean agreement per labelling field, each scored with the metric that fits its control: text similarity for transcripts, IoU for boxes, overlap for spans, exact match for choices."
          >
            <div className={block.elem("labels").toClassName()}>
              {d.field_agreement.map((r, i) => (
                <LabelRow
                  key={i}
                  name={r.field}
                  qualifier={r.metric}
                  value={r.agreement}
                  support={`${fmtInt(r.comparisons)} pairs`}
                />
              ))}
            </div>
          </AnalyticsPanel>
        )}

        {d && (d.per_label ?? []).length > 0 && (
          <AnalyticsPanel
            title="Label agreement"
            subtitle="How consistently annotators land on each label, most contested first. Low bars are where the guidelines need work."
          >
            <div className={block.elem("labels").toClassName()}>
              {d.per_label.slice(0, 24).map((r, i) => (
                <LabelRow
                  key={i}
                  name={r.label}
                  qualifier={r.field}
                  qualifierFirst
                  value={r.agreement}
                  support={`${fmtInt(r.support)} tasks`}
                />
              ))}
            </div>
          </AnalyticsPanel>
        )}

        {(d?.confusion ?? []).map((c) => (
          <AnalyticsPanel
            key={c.field}
            title={`Confusion, ${c.field}`}
            subtitle="Which labels get mixed up. The diagonal is agreement; bright cells off it are the confusions to fix."
          >
            <ConfusionHeatmap categories={c.categories} matrix={c.matrix} />
          </AnalyticsPanel>
        ))}

        {am.raters?.length >= 2 && (
          <AnalyticsPanel
            title="Agreement by annotator"
            subtitle="Pairwise agreement (%) between annotators. A row that is low all along flags a rater who drifts from the others."
          >
            <AnnotatorHeatmap am={am} />
          </AnalyticsPanel>
        )}

        {d && (d.hardest_tasks ?? []).length > 0 && (
          <AnalyticsPanel
            title="Hardest tasks"
            subtitle="The tasks annotators agreed on least: the first to review or to clarify in the guidelines."
          >
            <div className={block.elem("table-wrap").toClassName()}>
              <table className={block.elem("table").toClassName()}>
                <thead>
                  <tr>
                    <th>Task</th>
                    <th>Agreement</th>
                    <th>Annotations</th>
                  </tr>
                </thead>
                <tbody>
                  {d.hardest_tasks.map((t) => (
                    <tr key={t.task_id}>
                      <td>
                        <Link to={`/projects/${project.id}/data?task=${t.task_id}`}>Task {t.task_id}</Link>
                      </td>
                      <td>
                        <span
                          className={block.elem("chip").toClassName()}
                          style={{ background: agreementColor(t.agreement) }}
                        >
                          {fmtPct(t.agreement)}
                        </span>
                      </td>
                      <td>{fmtInt(t.annotations)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AnalyticsPanel>
        )}

        {extra}
      </AnalyticsGrid>
    </>
  );
};

// ── Readiness ─────────────────────────────────────────────────────────────────

const FACTOR_LABELS = {
  coverage: "Coverage",
  agreement: "Agreement",
  gt_coverage: "Ground-truth coverage",
  balance: "Class balance",
};

export const ReadinessSection = ({ project, extra = null }) => {
  const readiness = useMetrics(
    project?.id ? "projectMetricsReadiness" : null,
    project?.id ? { params: { pk: project.id } } : null,
    [project?.id],
  );
  if (!project?.id) return null;

  const rd = valid(readiness, "readiness");
  const score = rd?.readiness?.score;
  const factors = rd?.readiness?.factors ?? [];
  const balance = rd?.class_balance ?? [];
  const prf = rd?.prf_vs_gt;

  return (
    <>
      <SummaryStrip
        items={[
          {
            label: "Readiness",
            value: score == null ? "—" : fmtPct(score),
            sub: factors.length ? `weighted blend of the ${factors.length} factors` : undefined,
          },
          ...factors.map((f) => ({
            label: FACTOR_LABELS[f.key] || f.key,
            value: f.value == null ? "—" : fmtPct(f.value),
            sub: `weight ${Math.round(f.weight * 100)}%`,
          })),
        ]}
      />

      <AnalyticsGrid>
        <AnalyticsPanel
          title="Dataset readiness"
          subtitle="How close the dataset is to export or training quality: coverage, agreement, ground-truth coverage and class balance, weighted."
          empty={!rd ? (readiness.loading ? "Loading…" : "No data yet.") : null}
        >
          {rd && (
            <div className={block.elem("gauge").toClassName()}>
              <div className={block.elem("gauge-score").toClassName()} style={{ color: agreementColor(score) }}>
                {score == null ? "—" : `${Math.round(score * 100)}%`}
              </div>
              <div className={block.elem("gauge-factors").toClassName()}>
                {factors.map((f) => (
                  <LabelRow key={f.key} name={FACTOR_LABELS[f.key] || f.key} value={f.value} support="" />
                ))}
              </div>
            </div>
          )}
        </AnalyticsPanel>

        <AnalyticsPanel
          title="Class balance"
          subtitle="How evenly the classes of each field are spread (1 is even). Heavy skew hurts training."
          empty={balance.length === 0 ? "Appears once a field has labelled classes." : null}
        >
          {balance.length > 0 && (
            <div className={block.elem("labels").toClassName()}>
              {balance.map((b, i) => (
                <LabelRow
                  key={i}
                  name={b.field}
                  qualifier={`${b.classes.length} classes`}
                  value={b.balance}
                  support={b.classes[0] ? `top ${Math.round(b.classes[0].share * 100)}%` : ""}
                />
              ))}
            </div>
          )}
        </AnalyticsPanel>

        <AnalyticsPanel
          title="Accuracy against ground truth"
          subtitle={
            prf?.per_class?.length
              ? `Precision, recall and F1 per label against the reference answers. Macro F1 ${prf.macro_f1 == null ? "—" : fmtPct(prf.macro_f1)}.`
              : "Precision, recall and F1 per label against the reference answers."
          }
          empty={!prf || !prf.per_class?.length ? "Appears once some tasks have a ground-truth annotation." : null}
          span={extra ? 1 : 2}
        >
          {prf && prf.per_class?.length > 0 && (
            <div className={block.elem("table-wrap").toClassName()}>
              <table className={block.elem("table").toClassName()}>
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Precision</th>
                    <th>Recall</th>
                    <th>F1</th>
                    <th>Support</th>
                  </tr>
                </thead>
                <tbody>
                  {prf.per_class.map((c) => (
                    <tr key={c.label}>
                      <td>{c.label}</td>
                      <td>{fmtPct(c.precision)}</td>
                      <td>{fmtPct(c.recall)}</td>
                      <td>
                        <span className={block.elem("chip").toClassName()} style={{ background: agreementColor(c.f1) }}>
                          {fmtPct(c.f1)}
                        </span>
                      </td>
                      <td>{fmtInt(c.support)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AnalyticsPanel>

        {extra}
      </AnalyticsGrid>
    </>
  );
};

// ── Delivery ──────────────────────────────────────────────────────────────────

export const DeliverySection = ({ project, extra = null }) => {
  const delivery = useMetrics(
    project?.id ? "projectMetricsDelivery" : null,
    project?.id ? { params: { pk: project.id } } : null,
    [project?.id],
  );
  if (!project?.id) return null;

  const dv = valid(delivery, "forecast");
  const f = dv?.forecast ?? {};
  const target = dv?.target ?? null;
  const rq = dv?.review_queue ?? {};
  const lb = dv?.labor ?? {};
  const pct = f.percent_complete == null ? null : Math.round(f.percent_complete * 100);
  const window = f.window_days ?? 14;

  return (
    <>
      <SummaryStrip
        items={[
          {
            label: "Complete",
            value: pct == null ? "—" : `${pct}%`,
            sub: dv ? `${fmtInt(f.done ?? 0)} of ${fmtInt(f.required ?? 0)} annotations` : undefined,
          },
          { label: "Remaining", value: dv ? fmtInt(f.remaining ?? 0) : "—", sub: "annotations" },
          { label: "Velocity", value: dv ? `${f.velocity_per_day ?? 0} a day` : "—", sub: `last ${window} days` },
          {
            label: "Finish",
            value: f.eta_days == null ? "—" : f.eta_days === 0 ? "Done" : `${f.eta_days} days`,
            sub: f.eta_date || (f.eta_days == null && dv ? "needs more velocity" : undefined),
          },
          ...(target
            ? [
                {
                  label: "Target",
                  value: `${Math.min(100, Math.round((100 * target.tasks_done) / Math.max(1, target.tasks)))}%`,
                  sub: `${fmtInt(target.tasks_done)} of ${fmtInt(target.tasks)} tasks${target.deadline ? `, by ${target.deadline}` : ""}`,
                },
              ]
            : []),
        ]}
      />

      <AnalyticsGrid>
        {target && (
          <AnalyticsPanel
            title="Plan"
            subtitle={target.goal || "The target set when the project was made (Settings, General)."}
            span={2}
          >
            <div className={block.elem("label-bar").toClassName()} style={{ height: 14, marginBottom: 10 }}>
              <div
                className={block.elem("label-fill").toClassName()}
                style={{
                  width: `${Math.min(100, Math.round((100 * target.tasks_done) / Math.max(1, target.tasks)))}%`,
                  background: agreementColor(Math.min(1, target.tasks_done / Math.max(1, target.tasks))),
                }}
              />
            </div>
            <p className={block.elem("line").toClassName()}>
              {fmtInt(target.tasks_done)} of {fmtInt(target.tasks)} tasks have an annotation
              {target.languages?.length ? ` (${target.languages.join(", ")})` : ""}.{" "}
              {target.remaining === 0
                ? "The target is reached."
                : target.reach_days == null
                  ? `${fmtInt(target.remaining)} to go; no tasks were started in the last ${window} days, so no date yet.`
                  : `${fmtInt(target.remaining)} to go at ${target.tasks_per_day} tasks a day: reached in about ${target.reach_days} days (${target.reach_date}).`}
              {target.deadline
                ? target.on_track
                  ? ` On track for ${target.deadline}.`
                  : ` Not on track for ${target.deadline}.`
                : ""}
            </p>
          </AnalyticsPanel>
        )}

        <AnalyticsPanel
          title="Completion forecast"
          subtitle={`Projected from the last ${window} days of annotation velocity.`}
          empty={!dv ? (delivery.loading ? "Loading…" : "No data yet.") : null}
        >
          {dv && (
            <>
              {pct != null && (
                <div className={block.elem("label-bar").toClassName()} style={{ height: 14, marginBottom: 10 }}>
                  <div
                    className={block.elem("label-fill").toClassName()}
                    style={{ width: `${pct}%`, background: agreementColor(f.percent_complete) }}
                  />
                </div>
              )}
              <p className={block.elem("line").toClassName()}>
                {fmtInt(f.done ?? 0)} of {fmtInt(f.required ?? 0)} annotations done, {fmtInt(f.remaining ?? 0)}{" "}
                remaining, {f.velocity_per_day ?? 0} a day over the last {window} days
                {f.eta_days == null
                  ? ": no finish date until the velocity picks up."
                  : f.eta_days === 0
                    ? ": done."
                    : `: finished in about ${f.eta_days} days${f.eta_date ? ` (${f.eta_date})` : ""}.`}
              </p>
            </>
          )}
        </AnalyticsPanel>

        <AnalyticsPanel
          title="Review queue"
          subtitle="The backlog and how fast it is being cleared."
          empty={!dv ? (delivery.loading ? "Loading…" : "No data yet.") : null}
        >
          {dv && (
            <div className={block.elem("stats").toClassName()}>
              <Stat label="Open" value={fmtInt(rq.open ?? 0)} sub="waiting" />
              <Stat
                label="Oldest open"
                value={rq.oldest_age_days == null ? "—" : `${rq.oldest_age_days} days`}
                sub="since it was flagged"
              />
              <Stat label="Resolved" value={fmtInt(rq.resolved_7d ?? 0)} sub="last 7 days" />
            </div>
          )}
        </AnalyticsPanel>

        <AnalyticsPanel
          title="Labour"
          subtitle="Effort spent annotating, from each annotation's lead time."
          empty={!dv ? (delivery.loading ? "Loading…" : "No data yet.") : null}
          span={extra ? 1 : 2}
        >
          {dv && (
            <div className={block.elem("stats").toClassName()}>
              <Stat label="Hours" value={lb.total_hours ?? 0} sub={`${fmtInt(lb.annotations ?? 0)} annotations`} />
              <Stat label="Annotators" value={fmtInt(lb.active_annotators ?? 0)} sub="active" />
              <Stat
                label="Per annotator"
                value={lb.avg_hours_per_annotator == null ? "—" : `${lb.avg_hours_per_annotator} h`}
                sub="hours"
              />
            </div>
          )}
        </AnalyticsPanel>

        {extra}
      </AnalyticsGrid>
    </>
  );
};
